import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { COMMISSION, calcCommission } from '../lib/products'
import { useI18n } from '../lib/i18n'

/**
 * /story — "Watch how you earn" scripted simulation.
 * Same mechanic as the RiveSped demo: a ▶ play button runs a ~45s timed
 * script — chat messages arrive, deals close, counters tick up, toasts fire.
 * Built for influencer outreach: one link that SHOWS the money mechanics
 * instead of explaining them.
 */

type Msg = {
  id: number
  from: 'partner' | 'owner' | 'system'
  text: string
  sub?: string
}

type Step = {
  at: number // ms from play start
  msg?: Omit<Msg, 'id'>
  toast?: string
  deals?: number
  setupTotal?: number
  monthlyTotal?: number
  month?: number
  sixMonthTotal?: number
  done?: boolean
}

const starter = calcCommission(29)
const pro = calcCommission(59)

const SCRIPT: Step[] = [
  { at: 400, msg: { from: 'partner', text: 'Hi Maria! Quick one — can I show you something on my phone? Takes 2 minutes.' } },
  { at: 2600, msg: { from: 'owner', text: 'Sure, what is it? 😄' } },
  { at: 4600, msg: { from: 'partner', text: '☕ An AI assistant for Café Aroma — it takes orders, answers customers, runs a loyalty card. Look:', sub: 'getqordo.com/demo/coffee-bar' } },
  { at: 7400, toast: '👀 Maria is trying the live demo…' },
  { at: 9800, msg: { from: 'owner', text: 'Wait — it answered by itself?? And the loyalty card thing, my customers would love that.' } },
  { at: 12600, msg: { from: 'partner', text: `It handles all of it. Starter plan is €29/month, the setup is done for you. Want me to submit your interest? You pay nothing today.` } },
  { at: 15400, msg: { from: 'owner', text: 'OK — let’s do it. 👍' } },
  { at: 17200, msg: { from: 'system', text: '✅ Café Aroma signed up — Starter €29/mo' }, deals: 1 },
  { at: 18600, msg: { from: 'system', text: `💶 €${COMMISSION.setupFee} setup fee → 100% to YOU. Paid out on the 15th.` }, setupTotal: 30, toast: `+€${COMMISSION.setupFee} setup fee — yours` },
  { at: 21000, month: 1, monthlyTotal: starter.monthly, toast: `Month 1 — +€${starter.monthly.toFixed(2)} recurring` },
  { at: 23000, month: 2, monthlyTotal: starter.monthly * 2 },
  { at: 24600, msg: { from: 'system', text: '📣 Meanwhile: you showed the barbershop demo to Stefan. He picked Pro €59/mo.' }, deals: 2 },
  { at: 26200, msg: { from: 'system', text: `💶 Second €${COMMISSION.setupFee} setup fee → yours. Plus €${pro.monthly.toFixed(2)}/mo recurring.` }, setupTotal: 60, toast: `+€${COMMISSION.setupFee} setup fee — deal #2` },
  { at: 28400, month: 3, monthlyTotal: starter.monthly * 3 + pro.monthly },
  { at: 30200, month: 4, monthlyTotal: starter.monthly * 4 + pro.monthly * 2 },
  { at: 32000, month: 5, monthlyTotal: starter.monthly * 5 + pro.monthly * 3 },
  { at: 33800, month: 6, monthlyTotal: starter.monthly * 6 + pro.monthly * 4 },
  { at: 36000, msg: { from: 'system', text: `🏁 Six months, 2 deals, zero support work on your side: €60 setup + €${(starter.monthly * 6 + pro.monthly * 6).toFixed(2)} recurring.` }, sixMonthTotal: 60 + starter.monthly * 6 + pro.monthly * 6 },
  { at: 38400, msg: { from: 'system', text: '📈 Partners doing this 5x/month: €680+ over each 6-month window. Every demo is ready — you just show it.' } },
  { at: 40000, done: true, toast: 'That’s the whole model. Ready?' },
]

const TOTAL_MS = 41000

export default function Story() {
  const { t } = useI18n()
  const [playing, setPlaying] = useState(false)
  const [finished, setFinished] = useState(false)
  const [msgs, setMsgs] = useState<Msg[]>([])
  const [toast, setToast] = useState<string | null>(null)
  const [deals, setDeals] = useState(0)
  const [setupTotal, setSetupTotal] = useState(0)
  const [monthlyTotal, setMonthlyTotal] = useState(0)
  const [month, setMonth] = useState(0)
  const [progress, setProgress] = useState(0)
  const [sound, setSound] = useState(true)
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])
  const feedRef = useRef<HTMLDivElement>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const idRef = useRef(0)

  const clearTimers = () => {
    timers.current.forEach(clearTimeout)
    timers.current = []
  }

  useEffect(() => clearTimers, [])

  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight
  }, [msgs])

  function stopAudio() {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
  }

  function reset() {
    clearTimers()
    stopAudio()
    setMsgs([])
    setToast(null)
    setDeals(0)
    setSetupTotal(0)
    setMonthlyTotal(0)
    setMonth(0)
    setProgress(0)
    setFinished(false)
  }

  function play() {
    if (playing) {
      reset()
      setPlaying(false)
      return
    }
    reset()
    setPlaying(true)
    // Voiceover — starts on the user's ▶ gesture, so autoplay policies allow it.
    if (sound) {
      if (!audioRef.current) audioRef.current = new Audio('/audio/story-en.mp3')
      audioRef.current.currentTime = 0
      audioRef.current.play()?.catch(() => {})
    }
    const start = Date.now()
    const tick = setInterval(() => {
      const p = Math.min(100, ((Date.now() - start) / TOTAL_MS) * 100)
      setProgress(p)
      if (p >= 100) clearInterval(tick)
    }, 200)
    timers.current.push(tick as unknown as ReturnType<typeof setTimeout>)

    for (const step of SCRIPT) {
      timers.current.push(
        setTimeout(() => {
          if (step.msg) setMsgs((m) => [...m, { ...step.msg!, id: ++idRef.current }])
          if (step.toast) {
            setToast(step.toast)
            timers.current.push(setTimeout(() => setToast(null), 2400))
          }
          if (step.deals !== undefined) setDeals(step.deals)
          if (step.setupTotal !== undefined) setSetupTotal(step.setupTotal)
          if (step.monthlyTotal !== undefined) setMonthlyTotal(step.monthlyTotal)
          if (step.month !== undefined) setMonth(step.month)
          if (step.done) {
            setFinished(true)
            setPlaying(false)
          }
        }, step.at),
      )
    }
  }

  const totalEarned = setupTotal + monthlyTotal

  return (
    <section className="section py-10 max-w-5xl">
      <div className="text-center">
        <span className="chip bg-brand-500/10 text-brand-500 border border-brand-500/20">{t('45-second story')}</span>
        <h1 className="mt-4 text-3xl sm:text-5xl font-bold text-white">
          {t("Watch a partner's first six months")}
        </h1>
        <p className="mt-3 text-ink-500 max-w-2xl mx-auto">
          {t('No slides, no pitch deck. This is exactly what happens when you show one of our 51 ready demos to a local business owner — and what lands in your pocket.')}
        </p>
      </div>

      <div className="mt-8 grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* Chat feed */}
        <div className="card p-0 overflow-hidden flex flex-col" style={{ minHeight: 480 }}>
          <div className="flex items-center gap-3 border-b border-ink-800 px-5 py-4">
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${playing ? 'bg-emerald-400 animate-pulse' : 'bg-ink-700'}`} />
            <div>
              <div className="font-semibold text-white text-sm">{t('You → Maria (Café Aroma)')}</div>
              <div className="text-xs text-ink-500">{t('WhatsApp · a normal Tuesday')}</div>
            </div>
          </div>

          <div ref={feedRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3" style={{ maxHeight: 420 }}>
            {msgs.length === 0 && !playing && (
              <div className="h-full grid place-items-center py-16">
                <button onClick={play} className="btn-primary text-base px-8 py-4">
                  ▶ {t('Watch how you earn')}
                </button>
              </div>
            )}
            {msgs.map((m) =>
              m.from === 'system' ? (
                <div key={m.id} className="msg-in mx-auto max-w-[95%] rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-4 py-2.5 text-center text-sm text-emerald-300">
                  {m.text}
                </div>
              ) : (
                <div key={m.id} className={`msg-in max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.from === 'partner' ? 'ml-auto bg-brand-600 text-white rounded-br-md' : 'bg-ink-800 text-ink-100 rounded-bl-md'}`}>
                  {m.text}
                  {m.sub && (
                    <div className="mt-1.5 rounded-lg bg-black/25 px-2.5 py-1.5 text-xs font-mono text-sky-300">{m.sub}</div>
                  )}
                </div>
              ),
            )}
          </div>

          <div className="border-t border-ink-800 px-5 py-3">
            <div className="h-1 rounded-full bg-ink-800 overflow-hidden">
              <div className="h-full bg-brand-500 transition-all duration-200" style={{ width: `${progress}%` }} />
            </div>
            <div className="mt-2 flex items-center justify-between">
              <button onClick={play} className="text-sm font-semibold text-brand-400 hover:text-brand-300">
                {playing ? t('⏹ Stop') : msgs.length ? t('▶ Replay') : t('▶ Play the story')}
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setSound((s) => {
                      const next = !s
                      if (!next) stopAudio()
                      else if (playing && audioRef.current) audioRef.current.play()?.catch(() => {})
                      return next
                    })
                  }}
                  className="text-xs font-semibold text-ink-500 hover:text-white"
                  aria-label={sound ? 'Mute voiceover' : 'Unmute voiceover'}
                >
                  {sound ? t('🔊 Voice on') : t('🔇 Voice off')}
                </button>
                <span className="text-xs text-ink-500">{t('45 sec · real numbers')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Live earnings panel */}
        <div className="space-y-4">
          <div className="card">
            <div className="text-xs font-semibold uppercase tracking-wider text-ink-500">{t('Your earnings — live')}</div>
            <div className="mt-3 text-4xl font-bold text-emerald-400 tabular-nums">€{totalEarned.toFixed(2)}</div>
            <div className="mt-4 space-y-2.5 text-sm">
              <div className="flex justify-between"><span className="text-ink-500">{t('Deals closed')}</span><span className="font-semibold text-white tabular-nums">{deals}</span></div>
              <div className="flex justify-between"><span className="text-ink-500">{t('Setup fees (100% yours)')}</span><span className="font-semibold text-white tabular-nums">€{setupTotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-ink-500">{t('Recurring')} ({COMMISSION.recurringPercent}% × {COMMISSION.recurringMonths}mo)</span><span className="font-semibold text-white tabular-nums">€{monthlyTotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-ink-500">{t('Month')}</span><span className="font-semibold text-white tabular-nums">{month} / {COMMISSION.recurringMonths}</span></div>
            </div>
          </div>

          <div className="card border-brand-500/25">
            <div className="text-sm font-semibold text-white">{t('The formula')}</div>
            <p className="mt-2 text-sm text-ink-500 leading-relaxed">
              €{COMMISSION.setupFee} {t('setup (all yours) +')} {COMMISSION.recurringPercent}% {t('of the subscription, every month, for')} {COMMISSION.recurringMonths} {t('months. Per Pro-plan client that\'s')}{' '}
              <span className="text-emerald-400 font-semibold">€{pro.total6m.toFixed(2)}</span>.
            </p>
          </div>

          {finished && (
            <div className="card msg-in border-emerald-500/30 bg-emerald-500/5 text-center">
              <div className="text-lg font-bold text-white">{t('Your turn.')}</div>
              <p className="mt-1 text-sm text-ink-500">{t('Free to join. 51 demos ready to show today.')}</p>
              <div className="mt-4 grid gap-2">
                <Link to="/signup" className="btn-primary">{t('Join as partner — free')}</Link>
                <Link to="/catalog" className="btn-secondary">{t('Browse the demos')}</Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="msg-in fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl border border-emerald-500/30 bg-ink-900 px-5 py-3 text-sm font-semibold text-emerald-300 shadow-2xl">
          {toast}
        </div>
      )}
    </section>
  )
}
